import React from 'react';
import { toast } from 'sonner';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { SUBSCRIPTION_INVOICES_DATA } from '@shared/data/mockSubscriptions';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import type { TableActionGroup } from '@shared/types/data-table';
import type { SubscriptionInvoice, Subscription } from '@/features/subscriptions/types/subscriptions/subscription.types';

interface InvoicesSectionProps {
  isDetailView?: boolean;
  subscriptionData?: Subscription;
  invoices?: SubscriptionInvoice[];
  className?: string;
}

const renderTotal = (value: unknown, item: SubscriptionInvoice) => {
  const amount = typeof value === 'string' ? value : '$0.00';
  return (
    <div className="flex items-center gap-2">
      <span className="font-medium">{amount}</span>
      <span className="text-xs text-muted-foreground">{item.currency}</span>
    </div>
  );
};

const renderStatus = (_: unknown, item: SubscriptionInvoice) => (
  <div className="whitespace-nowrap">
    <AppStatusBadge
      entityType="invoice"
      status={item.status}
      size="sm"
    />
  </div>
);

const columns: PanelTableColumn<SubscriptionInvoice>[] = [
  {
    key: 'total',
    label: 'TOTAL',
    headerClassName: 'w-[120px]',
    render: renderTotal
  },
  {
    key: 'status',
    label: 'STATUS',
    headerClassName: 'w-[120px]',
    render: renderStatus
  },
  {
    key: 'invoiceNumber',
    label: 'INVOICE #',
    headerClassName: 'w-[120px]',
    cellClassName: 'font-medium'
  },
  {
    key: 'billingPeriod',
    label: 'BILLING PERIOD',
    headerClassName: 'w-[140px]'
  },
  {
    key: 'sent',
    label: 'SENT',
    headerClassName: 'w-20',
    cellClassName: 'whitespace-nowrap'
  },
  {
    key: 'due',
    label: 'DUE',
    headerClassName: 'w-20',
    cellClassName: 'whitespace-nowrap'
  },
  {
    key: 'finalized',
    label: 'FINALISED',
    headerClassName: 'w-[90px]',
    cellClassName: 'whitespace-nowrap'
  }
];

const rowActions = (_item: SubscriptionInvoice): TableActionGroup<SubscriptionInvoice>[] => {
  return [
    {
      label: "ACTIONS",
      actions: [
        {
          key: 'download-invoice',
          label: 'Download invoice',
          onClick: (invoice) => { void toast.info(`Download invoice ${invoice.invoiceNumber}`) }
        },
        {
          key: 'edit-invoice',
          label: 'Edit invoice',
          onClick: (invoice) => { void toast.info(`Edit invoice ${invoice.invoiceNumber}`) }
        },
        {
          key: 'duplicate-invoice',
          label: 'Duplicate invoice',
          onClick: (invoice) => { void toast.info(`Duplicate invoice ${invoice.invoiceNumber}`) }
        }
      ]
    },
    {
      label: "CONNECTIONS",
      actions: [
        {
          key: 'view-customer',
          label: 'View customer',
          onClick: () => { void toast.info('View customer') }
        }
      ]
    }
  ];
};

export const InvoicesSection = React.memo<InvoicesSectionProps>(({
  isDetailView = false,
  invoices,
  className,
}) => {
  const data = invoices ?? SUBSCRIPTION_INVOICES_DATA;

  return (
    <SectionCard title="Invoices" layout="down" className={className}>
      <PanelTable
        data={data}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No invoices found"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

InvoicesSection.displayName = 'InvoicesSection';
